export const WORKING_MODES = [
  { id: 'WFH', label: 'Work from home (WFH)' },
  { id: 'WFO', label: 'Work from office (WFO)' },
  { id: 'Hybrid', label: 'Hybrid' }
];

export const GENDER_OPTIONS = [
  { value: '', label: 'Select gender' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' }
];

export const CONTACT_CHANNELS = [
  { id: 'phone', label: 'Phone' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'email', label: 'Email' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'sms', label: 'SMS' }
];

import { isValidNoticePeriod } from '../constants/noticePeriod';
import { formatDateInputValue } from './candidateProfileUtils';
import { isValidPhone } from '../shared/phoneUtils';

export { digitsOnlyPhone, normalizeIndianMobileDigits, isValidPhone } from '../shared/phoneUtils';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const LINKEDIN_RE = /^(https?:\/\/)?(www\.)?linkedin\.com\//i;
const URL_RE = /^(https?:\/\/)?[\w.-]+\.[a-z]{2,}(\/\S*)?$/i;

export function isValidEmail(value) {
  const v = (value || '').trim();
  return v.length > 0 && EMAIL_RE.test(v);
}

export function isValidSalaryCtc(value, { required = false } = {}) {
  const raw = String(value ?? '').replace(/,/g, '').trim();
  if (!raw) return required ? false : true;
  if (!/^\d+$/.test(raw)) return false;
  return raw.length >= 6;
}

export function isValidOptionalUrl(value, { allowLinkedIn = false } = {}) {
  const v = (value || '').trim();
  if (!v) return true;
  if (/^https?:\/\/b\.?com\/?$/i.test(v) || /^b\.?com$/i.test(v.replace(/^https?:\/\//i, ''))) {
    return false;
  }
  if (allowLinkedIn && LINKEDIN_RE.test(v)) return true;
  if (v.includes('linkedin.com')) return LINKEDIN_RE.test(v) || v.startsWith('http');
  return URL_RE.test(v) || v.startsWith('http');
}

export { amountInWordsInr, salaryHintInr, budgetHintInr } from './amountInWordsInr';

/** Floating labels for annual CTC (Salary & Experience). */
export const SALARY_CURRENT_LABEL = 'Current salary / CTC (INR, per annum)';
export const SALARY_EXPECTED_LABEL = 'Expected salary / CTC (INR, per annum)';

function professionalComplete(professional) {
  const rows = Array.isArray(professional) ? professional : [];
  return rows.some(row => {
    const company = (row.company || row.employer || row.currentCompany || '').trim();
    const designation = (row.designation || row.title || row.role || '').trim();
    const startDate = formatDateInputValue(row.startDate);
    return company && designation && startDate;
  });
}

function validateOptionalFormats(form, errors) {
  if (form.contact?.linkedin && !isValidOptionalUrl(form.contact.linkedin, { allowLinkedIn: true })) {
    errors.linkedin = 'Enter a valid LinkedIn URL';
  }
  if (form.contact?.website && !isValidOptionalUrl(form.contact.website)) {
    errors.website = 'Enter a valid website URL';
  }
  if (form.currentSalary && !isValidSalaryCtc(form.currentSalary)) {
    errors.currentSalary = 'Current CTC must be at least 6 digits when provided';
  }
  if (form.expectedSalary && !isValidSalaryCtc(form.expectedSalary)) {
    errors.expectedSalary = 'Expected CTC must be at least 6 digits when provided';
  }
}

function validatePortalProfile(form, errors) {
  const fresher = !!form.isFresher;

  if (!formatDateInputValue(form.personal?.dob)) {
    errors.dob = 'Date of birth is required';
  }

  if (!fresher && !professionalComplete(form.professional)) {
    errors.experiencePath =
      'Add at least one professional entry (company, designation, start date) or check Fresher';
  }

  if (!fresher) {
    if (form.experience === '' || form.experience == null) {
      errors.experience = 'Experience (years) is required';
    } else if (Number(form.experience) <= 0 && !professionalComplete(form.professional)) {
      errors.experience = 'Add professional entries with start dates or enter experience years';
    }
    const workLocs = form.preferredWorkLocations || [];
    if (!workLocs.length && !(form.location || '').trim()) {
      errors.preferredWorkLocations = 'Add at least one preferred work location';
    }
    if (!(form.skills || []).length) {
      errors.skills = 'Select at least one core competency';
    }
    if (!(form.areasOfExpertise || []).length) {
      errors.areasOfExpertise = 'Select at least one area of expertise';
    }
    if (!isValidSalaryCtc(form.currentSalary, { required: true })) {
      errors.currentSalary = 'Current CTC must be at least 6 digits (₹1,00,000+)';
    }
  } else if (form.currentSalary && !isValidSalaryCtc(form.currentSalary)) {
    errors.currentSalary = 'Current CTC must be at least 6 digits when provided';
  }

  if (!(form.preferredWorkingModes || []).length) {
    errors.preferredWorkingModes = 'Select at least one preferred working mode';
  }
  if (!(form.contactChannels || []).length) {
    errors.contactChannels = 'Select at least one contact channel';
  }
  if (!isValidNoticePeriod(form.availability)) {
    errors.availability = 'Select a notice period (Immediate or days)';
  }
  if (!(form.personal?.name || '').trim()) {
    errors.name = 'Full name is required';
  }
}

/**
 * @param {'staff'|'portal'} mode — staff = admin/recruiter quick entry; portal = candidate self-service
 */
export function validateCandidateForm(form, { editing = false, mode = 'staff' } = {}) {
  const errors = {};

  if (!(form.name || form.personal?.name || '').trim()) {
    errors.name = 'Name is required';
  }
  if (!isValidEmail(form.email || form.contact?.email)) {
    errors.email = 'Valid email is required';
  }
  const phoneExactTen = mode === 'portal';
  if (!isValidPhone(form.phone || form.contact?.phone, { exactTen: phoneExactTen })) {
    errors.phone = phoneExactTen
      ? 'Enter a valid 10-digit Indian mobile (with or without +91)'
      : 'Phone must be 10–15 digits';
  }
  if (!editing && mode === 'staff' && !(form.password || '').trim()) {
    errors.password = 'Password is required for new candidates';
  }

  validateOptionalFormats(form, errors);

  if (mode === 'portal') {
    validatePortalProfile(form, errors);
  } else if (!form.isFresher && !professionalComplete(form.professional)) {
    errors.experiencePath =
      'Add professional details or check Fresher when the candidate has no work history';
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

export const EMPTY_CANDIDATE_FORM = {
  name: '',
  email: '',
  phone: '',
  password: 'password123',
  isFresher: false,
  preferredWorkingModes: [],
  contactChannels: [],
  personal: { name: '', dob: '', gender: '', maritalStatus: '', address: '', city: '', state: '', zip: '', country: 'India' },
  educational: [],
  professional: [],
  contact: { email: '', phone: '', linkedin: '', website: '' },
  skills: [],
  technicalSkills: [],
  currentSalary: '',
  expectedSalary: '',
  experience: '',
  preferredWorkLocations: [],
  location: '',
  availability: '',
  resumeLink: '',
  resumeOriginalName: '',
  profileHeading: '',
  areasOfExpertise: [],
  status: 'active',
  photo: '',
  photoPreview: null,
  photoFile: null,
  resumeFile: null,
  languagesKnown: []
};

